import { Icon, Tooltip } from '@dixa/lib-shared-habla';
import _orderBy from 'lodash/orderBy';
import { useCallback, useEffect } from 'react';
import Collapsible from 'react-collapsible';
import { useSelector } from 'react-redux';

import { numbersApi, PhoneNumber } from 'apis/numbers';
import type { TwilioTokenRegion } from 'app/middleware/twilio/voiceSdk/types';
import { RootReducerStateT } from 'app/reducers/rootReducer';
import { fromNumberInitializationStatus } from 'components/telephony/constants/fromNumberInitialization';
import { INITIAL_OWN_NUMBERS_LIMIT } from 'components/telephony/constants/ownNumbers';
import { useLazyPaginatedEndpoint } from 'utils/apis/pagination/useLazyPaginatedEndpoint';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { actions } from '../../../telephony/actions/telephony';
import { useSelectedFromNumberPresentation } from '../hooks/useSelectedFromNumberPresentation';

import ListNumbers from './ListNumbers';
import { PhoneNumbersCustomInput } from './PhoneNumbersCustomInput';

import '../styles/from-number-selector.less';

type OutboundNumberSelectorProps = {
  isOwnNumbersViewOpen: boolean;

  toggleOwnNumbersView: (isOpen: boolean) => void;

  /**
   * When set, every owned-numbers fetch (static, lazy paginated, search)
   * forwards `pbx_region` so only numbers in the given region appear. Used by
   * the call-transfer modal to keep the active conference in the same Twilio
   * datacenter. Leave undefined to preserve unrestricted topbar behavior.
   */
  restrictToRegion?: TwilioTokenRegion;
};

export const OutboundNumberSelector = ({
  isOwnNumbersViewOpen,
  toggleOwnNumbersView,
  restrictToRegion,
}: OutboundNumberSelectorProps): JSX.Element => {
  const dispatch = useAppDispatch();

  const selectedFromNumber = useSelector(
    (state: RootReducerStateT) => state.telephony.selectedFromNumber,
  );
  const initializationStatus = useSelector(
    (state: RootReducerStateT) =>
      state.telephony.fromNumberInitializationStatus,
  );

  // When the modal is region-restricted and the global selection lives in a
  // different region, treat it as missing so the trigger and list don't
  // present a number that would silently be sent on transfer.
  const resolvedSelectedFromNumber =
    selectedFromNumber !== null &&
    restrictToRegion !== undefined &&
    selectedFromNumber.pbx_region !== restrictToRegion
      ? null
      : selectedFromNumber;

  // Reads the first page of numbers for hide/show logic and the dropdown preview.
  // Initial from-number resolution is handled at layout level by
  // useInitializeTelephonyFromNumber, which subscribes with the same limit so
  // the two hooks share one RTK Query cache entry and the app fires a single
  // owned-numbers request on startup. When restricted to a region, we add
  // `pbxRegion` to scope the cache key independently from the global topbar.
  const {
    staticTelephoneNumbersArray,
    isStaticLoading,
    staticNumbersResponse,
  } = numbersApi.useReadNumbersQuery(
    { limit: INITIAL_OWN_NUMBERS_LIMIT, pbxRegion: restrictToRegion },
    {
      selectFromResult: ({ data, isLoading }) => ({
        staticTelephoneNumbersArray: data?.data?.numbers ?? [],
        isStaticLoading: isLoading,
        staticNumbersResponse: data,
      }),
    },
  );

  const {
    items: dynamicTelephoneNumbersArray,
    asyncStatus: dynamicAsyncStatus,
    hasNextPage,
    hasPreviousPage,
    nextPage,
    previousPage,
    changeArg,
  } = useLazyPaginatedEndpoint({
    endpoint: numbersApi.endpoints.readNumbers,
    itemsSelector: (data) => data.data.numbers,
  });

  useEffect(() => {
    if (dynamicAsyncStatus === 'uninitialized' || isOwnNumbersViewOpen) {
      changeArg({
        limit: 15,
        searchTerm: undefined,
        pbxRegion: restrictToRegion,
      });
    }
    // dynamicAsyncStatus is intentionally excluded — used as a one-time guard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwnNumbersViewOpen, changeArg, restrictToRegion]);

  useEffect(() => {
    // The region-filtered list is local to this instance — don't dispatch it
    // as the global "query numbers" or shared consumers (topbar list,
    // SearchUser, etc.) would shrink to the filtered subset.
    if (restrictToRegion !== undefined) return;

    if (dynamicAsyncStatus === 'fulfilled') {
      dispatch(
        actions.telephonyGetQueryNumbersSucceeded({
          data: { numbers: dynamicTelephoneNumbersArray },
          meta: {
            next_page: hasNextPage ? 'next' : null,
            prev_page: hasPreviousPage ? 'prev' : null,
          },
        }),
      );
    }
  }, [
    dynamicTelephoneNumbersArray,
    dynamicAsyncStatus,
    hasNextPage,
    hasPreviousPage,
    dispatch,
    restrictToRegion,
  ]);

  const orderedDynamicNumbers = _orderBy(dynamicTelephoneNumbersArray, [
    'e164_format',
  ]);

  const { fromNumberFlagCountryCode, fromNumberFormat } =
    useSelectedFromNumberPresentation(resolvedSelectedFromNumber);

  const changeFromNumber = useCallback(
    (fromNumber: PhoneNumber | null = null) => {
      if (fromNumber === null) {
        return;
      }

      dispatch(actions.telephonyChangeFromNumber({ number: fromNumber }));
    },
    [dispatch],
  );

  const onOwnNumberToggle = useCallback(() => {
    toggleOwnNumbersView(!isOwnNumbersViewOpen);
  }, [toggleOwnNumbersView, isOwnNumbersViewOpen]);

  const onSearch = useCallback(
    (searchTerm: string) => {
      changeArg(
        {
          searchTerm: searchTerm || undefined,
          limit: 15,
          pbxRegion: restrictToRegion,
        },
        { debounceTime: 300 },
      );
    },
    [changeArg, restrictToRegion],
  );

  // Require an actual response (not just !loading) so we never hide the
  // selector mid-fetch when the org might have numbers.
  const staticLoaded = Boolean(staticNumbersResponse) && !isStaticLoading;
  // Hide the selector entirely only in the unrestricted topbar case where the
  // org has no owned numbers. When restricted, keep it visible so the agent
  // sees the empty-state explanation and can search instead.
  const shouldHideSelector =
    restrictToRegion === undefined &&
    staticLoaded &&
    staticTelephoneNumbersArray.length === 0;
  // Restricted-modal empty state: the agent's global selection is in another
  // region, or there is no global selection and the static fetch returned no
  // candidates in this region. `staticLoaded` prevents flashing during fetch.
  const isRestrictedSelectionMissing =
    restrictToRegion !== undefined &&
    resolvedSelectedFromNumber === null &&
    staticLoaded;
  // Global "no outbound number" state: the layout-level init hook ran and
  // marked the chain as `missing`. Only shows when not restricted, since
  // `isRestrictedSelectionMissing` already covers the restricted-modal case.
  const isMissingFromNumber =
    restrictToRegion === undefined &&
    selectedFromNumber === null &&
    initializationStatus === fromNumberInitializationStatus.missing;
  const shouldShowLoadingTrigger =
    resolvedSelectedFromNumber === null &&
    !shouldHideSelector &&
    !isRestrictedSelectionMissing &&
    !isMissingFromNumber;

  let fromNumber: JSX.Element | null = null;
  let disabledPrevious = '';
  let disabledNext = '';
  let isHidden = '';
  let buttonsHidden = '';

  if (
    staticLoaded &&
    staticTelephoneNumbersArray.length > 0 &&
    dynamicAsyncStatus === 'fulfilled' &&
    orderedDynamicNumbers.length === 0
  ) {
    buttonsHidden = ' hidden';
  }

  // Match the legacy 'flag unknown' className when we can't resolve a region
  // so any future CSS targeting `.flag.unknown` keeps working.
  const flagClassName = `flag ${
    fromNumberFlagCountryCode?.toLowerCase() ?? 'unknown'
  }`;

  if (resolvedSelectedFromNumber && resolvedSelectedFromNumber.name) {
    fromNumber = (
      <div className="call-from flags">
        <p className="m-b-5 noselect">Outbound number</p>
        <Tooltip content="Outbound call recording is active">
          <span
            className={
              'call-recording ' +
              (resolvedSelectedFromNumber.record_outbound_calls === true
                ? ''
                : 'hidden')
            }
          >
            <Icon name="rec" />
          </span>
        </Tooltip>
        <span className={flagClassName}></span>
        <Tooltip side="bottom" content={resolvedSelectedFromNumber.name}>
          <span className="from-number-name">
            {fromNumberFormat} <em>({resolvedSelectedFromNumber.name})</em>
          </span>
        </Tooltip>
        <Icon
          name="angle-right"
          size={20}
          className="Collapsible__trigger-icon"
        />
      </div>
    );
  } else if (resolvedSelectedFromNumber) {
    fromNumber = (
      <div className="call-from flags">
        <p className="m-b-5 noselect">Outbound number</p>
        <Tooltip content="Outbound call recording is active">
          <span
            className={
              'call-recording ' +
              (resolvedSelectedFromNumber.record_outbound_calls === true
                ? ''
                : 'hidden')
            }
          ></span>
        </Tooltip>
        <span className={flagClassName}></span>
        <span>{fromNumberFormat}</span>
        <Icon
          name="angle-right"
          size={20}
          className="Collapsible__trigger-icon"
        />
      </div>
    );
  } else if (isRestrictedSelectionMissing) {
    fromNumber = (
      <div className="call-from flags">
        <p className="m-b-5 noselect">Outbound number</p>
        <Tooltip
          side="bottom"
          content="Pick an outbound number in the same region from the list below."
        >
          <span>No matching outbound number</span>
        </Tooltip>
        <Icon
          name="angle-right"
          size={20}
          className="Collapsible__trigger-icon"
        />
      </div>
    );
  } else if (isMissingFromNumber) {
    fromNumber = (
      <div className="call-from flags">
        <p className="m-b-5 noselect">Outbound number</p>
        <Tooltip
          side="bottom"
          content="No outbound number is set. Pick one from the list below."
        >
          <span>No outbound number</span>
        </Tooltip>
        <Icon
          name="angle-right"
          size={20}
          className="Collapsible__trigger-icon"
        />
      </div>
    );
  } else if (shouldShowLoadingTrigger) {
    fromNumber = (
      <div className="call-from flags">
        <p className="m-b-5 noselect">Outbound number</p>
        <span>Loading...</span>
        <Icon
          name="angle-right"
          size={20}
          className="Collapsible__trigger-icon"
        />
      </div>
    );
  }

  if (!hasPreviousPage) {
    disabledPrevious = 'disabled';
  }

  if (!hasNextPage) {
    disabledNext = 'disabled';
  }

  if (shouldHideSelector) {
    isHidden = ' hidden';
  }

  return (
    <div className={`collapsible-holder from-numbers-selector${isHidden}`}>
      <Collapsible
        handleTriggerClick={onOwnNumberToggle}
        open={isOwnNumbersViewOpen}
        trigger={fromNumber as any}
        transitionTime={300}
        easing="ease"
      >
        <li className="own-number-change dropdown-header" role="heading">
          Search outbound numbers
        </li>
        <PhoneNumbersCustomInput onSearch={onSearch} />
        <li
          className={`own-number-change${buttonsHidden} dropdown-header`}
          role="presentation"
        >
          Select outbound number
        </li>
        <ListNumbers
          dynamicTelephoneNumbersArray={orderedDynamicNumbers}
          onToggle={onOwnNumberToggle}
          selectedFromNumber={resolvedSelectedFromNumber}
          changeFromNumber={changeFromNumber}
        />
        {dynamicAsyncStatus === 'pending' ? (
          <ul className={`pager${buttonsHidden}`} style={{ margin: 'auto' }}>
            <li className="disabled pull-left">
              <a type="button">
                {' '}
                <span style={{ verticalAlign: '-2px' }}>
                  <Icon name="arrow-left" />
                </span>
                Loading{' '}
              </a>
            </li>
            <li className="disabled pull-right">
              <a type="button">
                {' '}
                Loading{' '}
                <span style={{ verticalAlign: '-2px' }}>
                  {' '}
                  <Icon name="arrow-right" />{' '}
                </span>
              </a>
            </li>
          </ul>
        ) : (
          <ul className={`pager${buttonsHidden}`} style={{ margin: 'auto' }}>
            <li className={disabledPrevious + ' previous'}>
              {!hasPreviousPage ? (
                <a type="button">
                  <span style={{ verticalAlign: '-2px' }}>
                    <Icon name="arrow-left" />
                  </span>
                  Prev Page
                </a>
              ) : (
                <a
                  type="button"
                  style={{ cursor: 'pointer' }}
                  onClick={() => previousPage()}
                >
                  <span style={{ verticalAlign: '-2px' }}>
                    <Icon name="arrow-left" />
                  </span>
                  Prev Page
                </a>
              )}
            </li>
            <li className={disabledNext + ' next'}>
              {!hasNextPage ? (
                <a className={buttonsHidden} type="button">
                  Next page
                  <span style={{ verticalAlign: '-2px' }}>
                    <Icon name="arrow-right" />
                  </span>
                </a>
              ) : (
                <a
                  className={buttonsHidden}
                  type="button"
                  style={{ cursor: 'pointer' }}
                  onClick={() => nextPage()}
                >
                  Next page
                  <span style={{ verticalAlign: '-2px' }}>
                    <Icon name="arrow-right" />
                  </span>
                </a>
              )}
            </li>
          </ul>
        )}
      </Collapsible>
      <li
        className="divider"
        role="separator"
        style={{ margin: '13px 0 0 0', width: '100%', listStyleType: 'none' }}
      />
    </div>
  );
};
